import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import type { ShopMember, LoginRequest } from '@shared/types';
import { saveToken, saveUser, clearSession } from '../utils';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  admin: ShopMember | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginAsDemo: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Fetch the shop_member profile for a given Supabase user ID.
 * Times out after 3 seconds to prevent infinite loading.
 */
async function fetchShopMember(userId: string): Promise<ShopMember | null> {
  console.log('[Auth] fetchShopMember: Starting query for userId:', userId);

  try {
    // Create a timeout promise
    const timeoutPromise = new Promise<ShopMember | null>((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout after 3s')), 3000)
    );

    // Race the query against the timeout
    const queryPromise = supabase
      .from('shop_members')
      .select('id, user_id, email, role, shop_id, is_active, created_at, shop:shops(is_onboarded)')
      .eq('user_id', userId)
      .single()
      .then(({ data, error }) => {
        if (error) throw error;
        // Data comes back as { ..., shop: { is_onboarded: boolean } | null } or array depending on relation type,
        // Since shop is a 1:1, it should be an object.
        return data as unknown as ShopMember;
      });

    const result = await Promise.race([queryPromise, timeoutPromise]);
    console.log('[Auth] fetchShopMember: Got data:', result);
    return result;
  } catch (err) {
    console.error('[Auth] fetchShopMember error:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<ShopMember | null>(null);
  const [isLoading, setIsLoading] = useState(true); // true only during initial session check
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // 1. Subscribe to auth state changes FIRST so we don't miss events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[Auth] onAuthStateChange:', event, session?.user?.email ?? 'no user');

      if (!mounted) return;

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('[Auth] SIGNED_IN - fetching shop member for', session.user.id);
        const member = await fetchShopMember(session.user.id);
        console.log('[Auth] fetchShopMember result:', member);
        if (!mounted) return;
        if (member) {
          console.log('[Auth] Saving token and user, setting isLoading=false');
          saveToken(session.access_token);
          saveUser(member);
          setAdmin(member);
          setIsLoading(false);
        } else {
          console.warn('[Auth] No shop member found for this user, but clearing loading anyway');
          setIsLoading(false);
        }
      } else if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        console.log('[Auth] SIGNED_OUT or TOKEN_REFRESHED without session');
        clearSession();
        setAdmin(null);
        setIsLoading(false);
      } else if (event === 'INITIAL_SESSION') {
        console.log('[Auth] INITIAL_SESSION event');
        // Don't clear loading on INITIAL_SESSION - wait for other events
      }
    });

    // 2. Check for an existing session (page reload / return visit)
    const initializeAuth = async () => {
      console.log('[Auth] Checking existing session...');
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[Auth] getSession error:', sessionError.message);
          if (mounted) setIsLoading(false);
          return;
        }

        if (session?.user) {
          console.log('[Auth] Found existing session for', session.user.email);
          // onAuthStateChange will fire INITIAL_SESSION — let it handle state
          // but if it doesn't fire quickly, handle here as fallback
          const member = await fetchShopMember(session.user.id);
          if (!mounted) return;
          if (member) {
            saveToken(session.access_token);
            saveUser(member);
            setAdmin(member);
          }
        } else {
          console.log('[Auth] No existing session');
        }
      } catch (err) {
        console.error('[Auth] initializeAuth error:', err);
      } finally {
        if (mounted) {
          console.log('[Auth] Initial auth check complete, clearing loading');
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setError(null);
    // Don't set isLoading here — let onAuthStateChange drive state
    // Just attempt the sign in and let onAuthStateChange handle the rest

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (signInError) {
      console.error('[Auth] signInWithPassword error:', signInError.message);
      setError(signInError.message);
      throw signInError;
    }

    console.log('[Auth] signInWithPassword succeeded, waiting for onAuthStateChange...');
    // onAuthStateChange SIGNED_IN event will update admin state
  }, []);

  const loginWithGoogle = useCallback(async (): Promise<void> => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });

    if (signInError) {
      console.error('[Auth] signInWithOAuth error:', signInError.message);
      setError(signInError.message);
      throw signInError;
    }
  }, []);

  const loginAsDemo = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      const demoShopMember: ShopMember = {
        id: 'demo-admin',
        user_id: 'demo-user-id',
        email: 'owner@printnow.local',
        role: 'owner',
        shop_id: 'shop-local',
        is_active: true,
        last_login_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
      };
      saveToken('demo-local-token');
      saveUser(demoShopMember);
      setAdmin(demoShopMember);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    try {
      console.log('[Auth] Signing out...');
      await supabase.auth.signOut();
      // onAuthStateChange SIGNED_OUT event will clear admin state
    } catch (err) {
      console.error('[Auth] Logout error:', err);
      // Still clear local session even if signOut fails
      clearSession();
      setAdmin(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    error,
    login,
    loginWithGoogle,
    loginAsDemo,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};